"use client";

import React, { useEffect, useLayoutEffect, useState, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import {
  Dialog as HeadlessDialog,
  DialogTitle as HeadlessDialogTitle,
  DialogDescription as HeadlessDialogDescription,
} from "@headlessui/react";
import clsx from "clsx";
import { motion, AnimatePresence } from "motion/react";
import { useIsOsMode, useWindowId } from "@/components/ui/os-layout";
import {
  getOsWindowContentElement,
  getOsWindowOverlayPortalElement,
} from "@/components/ui/os-layout/osOverlayPortal";

/** When set (e.g. catalog demo preview), overlays cover this element instead of the viewport. */
export const OverlayContainmentContext = createContext<HTMLElement | null>(null);

/** @deprecated Prefer OverlayContainmentContext */
export const DialogContainmentContext = OverlayContainmentContext;

// Context to share OS / contained mode state between Dialog and its children
const DialogModeContext = createContext<{ isInOsMode: boolean; usePlainChrome: boolean }>({
  isInOsMode: false,
  usePlainChrome: false,
});
const useDialogMode = () => useContext(DialogModeContext);

const sizes = {
  xs: "sm:w-xs",
  sm: "sm:w-sm",
  md: "sm:w-md",
  lg: "sm:w-lg",
  xl: "sm:w-xl",
  "2xl": "sm:w-2xl",
  "3xl": "sm:w-3xl",
  "4xl": "sm:w-4xl",
  "5xl": "sm:w-5xl",
};

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: keyof typeof sizes;
  className?: string;
  windowless?: boolean;
  /** Override default backdrop tint / blur (e.g. review overlay on home). */
  backdropClassName?: string;
  /** Center over the viewport even in OS mode (e.g. dialogs opened from the workspace banner). */
  pinToViewport?: boolean;
}

export function Dialog({
  open,
  onClose,
  children,
  size = "md",
  className,
  windowless = false,
  backdropClassName,
  pinToViewport = false,
}: DialogProps & { className?: string }) {
  const [mounted, setMounted] = useState(false);
  const [osPortalTarget, setOsPortalTarget] = useState<HTMLElement | null>(null);
  const isInOsMode = useIsOsMode() && !pinToViewport;
  const windowId = useWindowId();
  const containmentTarget = useContext(OverlayContainmentContext);
  const isContained = Boolean(containmentTarget) && !isInOsMode;
  const useLocalOverlay = isInOsMode || isContained;

  useEffect(() => {
    if (open && isInOsMode && windowId) {
      // Prevent the active OS window content from scrolling while dialog is open.
      const windowContent = getOsWindowContentElement(windowId);
      if (windowContent) {
        const originalOverflow = windowContent.style.overflow;
        windowContent.style.overflow = "hidden";

        return () => {
          windowContent.style.overflow = originalOverflow;
        };
      }
    }
  }, [open, isInOsMode, windowId]);

  useLayoutEffect(() => {
    if (!isInOsMode) {
      setOsPortalTarget(null);
      return;
    }

    if (!windowId) {
      setOsPortalTarget(null);
      return;
    }

    setOsPortalTarget(getOsWindowOverlayPortalElement(windowId));
  }, [isInOsMode, windowId, open]);

  useEffect(() => {
    // Ensure we're on the client side
    if (typeof window !== "undefined") {
      const mainContent: HTMLElement = document.body;
      if (mainContent) {
        // Create portal root if it doesn't exist
        let portalRoot = mainContent.querySelector(
          "#headlessui-portal-root"
        ) as HTMLElement | null;
        if (!portalRoot) {
          portalRoot = document.createElement("div");
          portalRoot.id = "headlessui-portal-root";
          portalRoot.style.position = "relative";
          portalRoot.style.zIndex = "9999";
          mainContent.appendChild(portalRoot);
        }
        setMounted(true);
      }
    }
  }, []);

  if (!mounted) return null;

  const wrapperVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        staggerChildren: 0.2,
      },
    },
    exit: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        staggerDirection: -1,
        when: "afterChildren",
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const dialogVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        damping: 25,
        stiffness: 300,
        mass: 0.8,
      },
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };

  // Shared dialog content structure
  const dialogContent = (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          key="dialog-wrapper"
          variants={wrapperVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={clsx(
            useLocalOverlay ? "absolute" : "fixed",
            "inset-0 flex min-h-0 items-center justify-center",
            windowless ? "p-0" : "p-4",
            isInOsMode ? "z-[99999]" : (className && className.includes("z-") ? className : "z-[60]")
          )}
        >
          <motion.div
            className={clsx(
              useLocalOverlay ? "absolute" : "fixed",
              "inset-0 z-[55]",
              isInOsMode ? "bg-slate-900/30" : "bg-slate-900/30 dark:bg-slate-900/70",
              backdropClassName,
            )}
            variants={backdropVariants}
            aria-hidden="true"
            onClick={onClose}
          />
          <motion.div
            className={clsx(
              "relative z-[60] mx-auto flex min-h-0 flex-col",
              isInOsMode || isContained ? "max-h-full" : "max-h-[80vh]",
              windowless
                ? "rounded-none bg-transparent shadow-none"
                : "rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/30",
              sizes[size],
              className
            )}
            variants={dialogVariants}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return isInOsMode ? (
    // OS Mode: Portal to window content so dialogs center over the whole window.
    open ? (
      osPortalTarget ? createPortal(
        <DialogModeContext.Provider value={{ isInOsMode: true, usePlainChrome: true }}>
          <div
            className="absolute inset-0 z-[99999]"
            data-testid="dialog-os-root"
          >
            {dialogContent}
          </div>
        </DialogModeContext.Provider>,
        osPortalTarget,
      ) : (
        <DialogModeContext.Provider value={{ isInOsMode: true, usePlainChrome: true }}>
          <div
            className="absolute inset-0 z-[99999]"
            data-testid="dialog-os-root"
          >
            {dialogContent}
          </div>
        </DialogModeContext.Provider>
      )
    ) : null
  ) : isContained && containmentTarget ? (
    // Contained Mode: Portal into a local host (e.g. catalog preview) instead of the viewport.
    open ? (
      createPortal(
        <DialogModeContext.Provider value={{ isInOsMode: false, usePlainChrome: true }}>
          <div
            className="absolute inset-0 z-[60]"
            data-testid="dialog-contained-root"
          >
            {dialogContent}
          </div>
        </DialogModeContext.Provider>,
        containmentTarget,
      )
    ) : null
  ) : (
    // Classic Mode: Use HeadlessDialog with portal
    <DialogModeContext.Provider value={{ isInOsMode: false, usePlainChrome: false }}>
      <HeadlessDialog
        as="div"
        static
        open={open}
        onClose={onClose}
        className={clsx("relative z-[60]", className)}
      >
        {dialogContent}
      </HeadlessDialog>
    </DialogModeContext.Provider>
  );
}

export function DialogTitle({ children }: { children: React.ReactNode }) {
  const { usePlainChrome } = useDialogMode();
  
  return usePlainChrome ? (
    // OS / contained: Render as plain div without HeadlessUI context
    <div className="flex-shrink-0 px-6 py-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
      {children}
    </div>
  ) : (
    // Classic Mode: Use HeadlessUI DialogTitle
    <HeadlessDialogTitle className="flex-shrink-0 px-6 py-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
      {children}
    </HeadlessDialogTitle>
  );
}

export function DialogBody({
  children,
  className,
  windowless = false,
}: {
  children: React.ReactNode;
  className?: string;
  windowless?: boolean;
}) {
  return (
    <div
      className={clsx(
        windowless
          ? "min-h-0 flex-1 overflow-y-auto text-slate-700 dark:text-slate-200"
          : "min-h-0 flex-1 overflow-y-auto px-6 py-4 pt-0 text-slate-700 dark:text-slate-200",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DialogActions({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-200 px-6 py-4 dark:border-slate-600">
      {children}
    </div>
  );
}

export function DialogDescription({
  className,
  children,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<typeof HeadlessDialogDescription>,
  "as" | "className"
>) {
  const { usePlainChrome } = useDialogMode();
  
  return usePlainChrome ? (
    <p
      {...(props as React.HTMLAttributes<HTMLParagraphElement>)}
      className={clsx(className, "mt-2 flex-shrink-0 px-6 text-pretty text-sm text-slate-600 dark:text-slate-400")}
    >
      {typeof children === "function" ? children({}) : children}
    </p>
  ) : (
    <HeadlessDialogDescription
      className={clsx(className, "mt-2 flex-shrink-0 px-6 text-pretty text-sm text-slate-600 dark:text-slate-400")}
      {...props}
    >
      {children}
    </HeadlessDialogDescription>
  );
}
